import { cx } from "../../components/ui";
import "./raport-cetak.css";

// Template raport versi 1 — susunan & desain raport KIP Report System v1.9.1 (rancangan §7.1):
// Student Information, penilaian per aspek, Overall Progress, Monthly Progress Summary.
//
// Isinya HANYA dari `isi` (snapshot yang disusun server), tak pernah dihitung di sini: raport
// terbit dicetak ulang kapan pun hasilnya sama, dan pratinjau = yang akan terbit. Bahasa
// raport tetap Inggris seperti raport lama; kalimat komentar trainer berbahasa Indonesia.
//
// Tambahan dibanding raport lama: baris Student's Name & Period, nama bulan di tiap kartu
// bulan, dan bulan kosong tertulis "Belum mengikuti kelas" (keputusan #11). Sejak 2 Nov 2026
// raport SD/SMP/SMA memuat Speaking DAN Learning Record: dua halaman, satu per bagian.

const BULAN_EN = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const KE = ["First Month", "Second Month", "Third Month"];
const JUDUL = {
  speaking: { sub: "STUDENT SPEAKING PROGRESS REPORT", bagian: "Speaking Assessment", level: "Current Level" },
  learning: { sub: "STUDENT LEARNING RECORD & PROGRESS REPORT", bagian: "Learning Record", level: "Class Level" }
};

const bagiIso = (iso) => String(iso).split("-").map(Number);
const bulanEn = (iso) => { const [y, m] = bagiIso(iso); return `${BULAN_EN[m - 1] || ""} ${y}`; };

/** ["2026-10", …, "2026-12"] → "October – December 2026"; lintas tahun menyebut keduanya. */
export function periodeEn(bulan) {
  const [y1, m1] = bagiIso(bulan[0]);
  const [y3, m3] = bagiIso(bulan[bulan.length - 1]);
  return y1 === y3
    ? `${BULAN_EN[m1 - 1]} – ${BULAN_EN[m3 - 1]} ${y3}`
    : `${BULAN_EN[m1 - 1]} ${y1} – ${BULAN_EN[m3 - 1]} ${y3}`;
}

function Sorotan({ judul, data }) {
  return (
    <div className="rp-kotak">
      <h4>{judul}</h4>
      <p>
        {data ? <><b>{data.label}</b><br />{data.teks}</> : "Assessment has not been completed."}
      </p>
    </div>
  );
}

/** Satu halaman raport untuk satu bagian (Speaking atau Learning Record). */
function Halaman({ isi, bagian, labelLevel, tanda, ke, dari }) {
  const j = JUDUL[bagian.rubrik] || JUDUL.learning;
  const label = Object.fromEntries(bagian.aspek.map((a) => [a.kode, a.label]));
  const level = isi.level && isi.level.level
    ? [isi.level.level, isi.level.keterangan].filter(Boolean).join(" — ")
    : "—";

  return (
    <article className="rp-lembar" aria-label={`Raport ${isi.murid.nama}${dari > 1 ? ` — ${j.bagian}` : ""}`}>
      <div className="rp-raport">
        {tanda && (
          <div className={cx("rp-tanda", tanda === "DIBATALKAN" && "rp-tanda-batal")} aria-hidden="true">
            <span>{tanda}</span>
          </div>
        )}

        <header className="rp-judul">
          <div className="rp-emblem">★<br />STUDENT<br />REPORT</div>
          <div className="rp-judul-tengah">
            <h2>REPORT OF PROGRESS</h2>
            <p>{j.sub}</p>
          </div>
          <div className="rp-logo">
            <img src={`${process.env.PUBLIC_URL || ""}/logo-kip.jpeg`} alt="KIP — Kampung Inggris Pontianak" />
          </div>
        </header>

        {/* Nomor halaman di samping pita, bukan baris sendiri: halaman Learning Record dengan
            kalimat terpanjang tinggal ±20 px dari batas satu A4. */}
        <div className="rp-pita-baris">
          <div className="rp-pita">◆ Student Information</div>
          {dari > 1 && <span className="rp-halaman">Page {ke} of {dari}</span>}
        </div>
        <div className="rp-info">
          <div className="rp-info-kiri">
            <dl className="rp-isian">
              <dt>Student's Name</dt>
              <dd>{isi.murid.nama}</dd>
              <dt>Tutor's Name</dt>
              <dd>{isi.trainer.nama || "—"}</dd>
              <dt>Class Name</dt>
              <dd>{[isi.kelas.nama, isi.kelas.level, isi.kelas.program].filter(Boolean).join(" • ")}</dd>
              <dt>{labelLevel || j.level}</dt>
              <dd>{level}</dd>
              <dt>Period</dt>
              <dd>{periodeEn(isi.term.bulan)}</dd>
            </dl>
          </div>
          <div className="rp-foto">
            <div className="rp-foto-bingkai" aria-hidden="true"><span>◉</span></div>
            <div className="rp-foto-label">Student Photo</div>
          </div>
        </div>

        <section>
          <div className="rp-pita">◆ {j.bagian}</div>
          <table className="rp-tabel">
            <thead>
              <tr>
                <th style={{ width: "4.6em" }}>No.</th>
                <th>Aspects</th>
                <th style={{ width: "15em" }}>Grade</th>
              </tr>
            </thead>
            <tbody>
              {bagian.akhir.map((a, i) => (
                <tr key={a.aspek}>
                  <td>{i + 1}</td>
                  <td>{label[a.aspek] || a.aspek}</td>
                  <td>{a.label || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section>
          <div className="rp-pita">◆ Overall Progress</div>
          <div className="rp-dua">
            <Sorotan judul="Strength Point" data={bagian.kekuatan} />
            <Sorotan judul="Areas for Improvement" data={bagian.kelemahan} />
            <div className="rp-trainer">
              <small>Trainer:</small>
              <b>{isi.trainer.nama || "—"}</b>
            </div>
          </div>
        </section>

        <section>
          <div className="rp-pita">◆ Monthly Progress Summary</div>
          <div className="rp-bulan-daftar">
            {bagian.bulan.map((b, i) => {
              // Bulan kosong dinilai atas SELURUH raport: bulan yang kedua bagiannya kosong.
              const kosong = ((isi.bulan || [])[i] || b).status === "kosong";
              return (
                <div key={b.ke} className="rp-bulan">
                  <div className="rp-bulan-kiri">
                    <h4>{KE[i]} · {bulanEn(isi.term.bulan[i])}</h4>
                    <div className={cx("rp-mini", bagian.aspek.length > 4 && "rp-mini-enam")}>
                      {bagian.aspek.map((a) => (
                        <div key={a.kode} className="rp-mini-sel">
                          <span>{a.label}</span>
                          <b>{b.nilai[a.kode] || "—"}</b>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={cx("rp-komentar", kosong && "rp-komentar-kosong")}>
                    <small>Teacher's Comment</small>
                    <p>{kosong ? "Belum mengikuti kelas" : b.komentar || "Teacher comment has not been completed yet."}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </article>
  );
}

/**
 * @param isi   snapshot raport (lihat src/utils/raport/susun.js)
 * @param tanda "PRATINJAU" | "DIBATALKAN" | null — tanda air
 *
 * Template v2: satu halaman per bagian (`isi.bagian`: Speaking, lalu Learning Record).
 * Raport terbit v1 hanya punya satu bagian di tingkat atas — tetap dicetak seperti dulu.
 */
export default function TemplateRaport({ isi, tanda = null }) {
  const bagian = Array.isArray(isi.bagian) && isi.bagian.length
    ? isi.bagian
    : [{ rubrik: isi.rubrik, aspek: isi.aspek, akhir: isi.akhir, bulan: isi.bulan, kekuatan: isi.kekuatan, kelemahan: isi.kelemahan }];
  const labelLevel = isi.level && isi.level.jenis
    ? (isi.level.jenis === "dihitung" ? "Current Level" : "Class Level")
    : null;
  return (
    <div className="rp-halaman-daftar">
      {bagian.map((b, i) => (
        <Halaman key={b.rubrik || i} isi={isi} bagian={b} labelLevel={labelLevel} tanda={tanda} ke={i + 1} dari={bagian.length} />
      ))}
    </div>
  );
}
